import { useEffect } from "react";
import { ChefHat, CalendarDays, PackageOpen, ShoppingBasket, Sparkles, Recycle, Smartphone, Check, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";


const FAQ_ITEMS = [
  {
    q: "Kan ik Smartly gratis gebruiken?",
    a: "Ja. Smartly Free geeft je basisplanning, een eenvoudige boodschappenlijst en plek voor maximaal 20 pantry-items. Daar komt geen creditcard bij kijken.",
  },
  {
    q: "Wat krijg ik met Premium?",
    a: "Volledige weekplanning (zeven dagen), onbeperkte pantry, onbeperkt opgeslagen recepten, slimmere boodschappenlijst en de premium plannings-intelligentie. Verder geen extra ruis.",
  },
  {
    q: "Kan ik maandelijks opzeggen?",
    a: "Ja. Je beheert je abonnement zelf via Stripe vanuit je profiel. Opzeggen kan op elk moment en gaat in op het einde van je lopende periode.",
  },
  {
    q: "Is Smartly een dieet-app?",
    a: "Nee. Smartly helpt je plannen, voorraad bijhouden en boodschappen doen — wij schrijven geen diëten voor. Wel kun je je calorie- en koolhydratendoel zelf instellen.",
  },
  {
    q: "Werkt Smartly op iPhone en Android?",
    a: "Ja. Smartly is een mobielvriendelijke web-app (PWA). Open hem in Safari of Chrome en je kunt hem desgewenst aan je beginscherm toevoegen.",
  },
  {
    q: "Wanneer kan ik Premium activeren?",
    a: "Premium is beschikbaar voor ingelogde gebruikers via het profiel. Je kunt eerst gratis starten en later upgraden.",
  },
];

const FEATURES = [
  {
    icon: CalendarDays,
    title: "Weekplanner",
    body: "Eén dag tegelijk in zicht. Lock wat je wilt houden, swap wat niet past, en plan zonder gedoe.",
  },
  {
    icon: PackageOpen,
    title: "Pantry-aware planning",
    body: "Smartly weet wat je in huis hebt en stelt maaltijden voor die je voorraad eerst opmaken.",
  },
  {
    icon: ShoppingBasket,
    title: "Slimme boodschappenlijst",
    body: "Automatisch samengesteld op basis van je weekmenu, met dubbelingen eruit en pack-sizes erin.",
  },
  {
    icon: Recycle,
    title: "Minder verspilling",
    body: "Houdbaarheidsdata, leftovers en pantry-tracking helpen om eten niet te laten staan.",
  },
  {
    icon: Smartphone,
    title: "Mobiel klaar",
    body: "Werkt direct in je browser en is geoptimaliseerd voor telefoon. Voeg toe aan je beginscherm voor een app-gevoel.",
  },
];

const FREE_BULLETS = [
  "Basisplanning",
  `Tot 3 planner-dagen`,
  `Tot 20 pantry-items`,
  "Basis boodschappenlijst",
];

const PREMIUM_BULLETS = [
  "Volledige weekplanning",
  "Onbeperkte pantry",
  "Onbeperkt opgeslagen recepten",
  "Slimmere boodschappenlijst",
  "Premium plannings-intelligentie",
];


export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  // /pricing -> anchor scroll to #pricing on the same landing surface.
  useEffect(() => {
    if (location.hash) {
      const node = document.getElementById(location.hash.replace("#", ""));
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else if (location.pathname === "/pricing") {
      const node = document.getElementById("pricing");
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash, location.pathname]);

  const startCtaTarget = user ? "/" : "/signup";
  const premiumCtaTarget = user ? "/profile#premium-card" : "/signup";

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,209,102,0.28),_transparent_38%),linear-gradient(180deg,_#fbf7ef_0%,_#f4efe3_50%,_#edf3ea_100%)] text-stone-900" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-stone-200/70 bg-[#fbf7ef]/85 backdrop-blur" data-testid="landing-nav">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-stone-900" data-testid="landing-nav-logo">
            <span className="grid h-9 w-9 place-items-center rounded-2xl bg-emerald-700 text-white">
              <ChefHat className="h-5 w-5" />
            </span>
            <span className="text-base font-semibold tracking-tight">Smartly</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/login" className="hidden text-sm font-medium text-stone-700 hover:text-stone-900 sm:inline" data-testid="landing-nav-login">
              Log in
            </Link>
            <Button onClick={() => navigate(startCtaTarget)} className="h-10 rounded-full bg-emerald-700 px-4 text-white hover:bg-emerald-800" data-testid="landing-nav-cta">
              {user ? "Open app" : "Start gratis"}
            </Button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 pb-20">
        {/* Hero */}
        <section className="grid items-center gap-10 py-14 md:grid-cols-[1.1fr_0.9fr] md:py-20" data-testid="landing-hero">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700" data-testid="landing-hero-kicker">
              <Sparkles className="h-3.5 w-3.5" />
              Smartly
            </p>
            <h1 className="mt-5 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-hero-headline">
              Plan je maaltijden, boodschappen en voorraad in één slimme weekplanner.
            </h1>
            <p className="mt-5 max-w-xl text-base text-stone-600 sm:text-lg" data-testid="landing-hero-subheadline">
              Smartly helpt je minder nadenken over eten, minder vergeten in de winkel en beter gebruiken wat je al in huis hebt.
            </p>
            <div className="mt-7 flex flex-wrap gap-3" data-testid="landing-hero-ctas">
              <Button
                onClick={() => navigate(startCtaTarget)}
                className="h-12 rounded-full bg-emerald-700 px-6 text-white hover:bg-emerald-800"
                disabled={loading}
                data-testid="landing-hero-primary-cta"
              >
                {user ? "Open app" : "Start gratis"}
              </Button>
              <Button
                onClick={() => navigate(user ? "/" : "/login")}
                variant="outline"
                className="h-12 rounded-full border-stone-300 bg-white px-6 text-stone-800 hover:bg-stone-50"
                data-testid="landing-hero-secondary-cta"
              >
                {user ? "Naar dashboard" : "Log in"}
              </Button>
            </div>
            <p className="mt-4 text-xs text-stone-500" data-testid="landing-hero-fineprint">
              Gratis starten. Geen creditcard nodig.
            </p>
          </div>

          {/* In-app preview card (no fake screenshots — uses real styling) */}
          <div className="relative" data-testid="landing-hero-preview">
            <div className="rounded-[32px] border border-stone-200/80 bg-white/85 p-5 shadow-[0_30px_80px_rgba(44,62,38,0.12)] backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700">Weekplanner</p>
              <h3 className="mt-2 text-lg font-semibold text-stone-900">Maandag · vandaag</h3>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between rounded-2xl bg-emerald-50/70 px-3 py-2.5">
                  <span className="font-medium text-stone-800">Ontbijt</span>
                  <span className="text-xs text-stone-500">5 min prep</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-amber-50/70 px-3 py-2.5">
                  <span className="font-medium text-stone-800">Lunch</span>
                  <span className="text-xs text-stone-500">15 min prep</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-stone-100/80 px-3 py-2.5">
                  <span className="font-medium text-stone-800">Diner</span>
                  <span className="text-xs text-stone-500">25 min prep</span>
                </div>
              </div>
              <div className="mt-5 flex gap-2 text-xs">
                <span className="rounded-full bg-emerald-700 px-3 py-1 font-semibold text-white">Pantry-aware</span>
                <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700">2 leftovers</span>
              </div>
            </div>
          </div>
        </section>

        {/* Problem */}
        <section className="rounded-[32px] border border-stone-200 bg-white/80 p-7 backdrop-blur sm:p-10" data-testid="landing-problem">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500">Waarom Smartly?</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="landing-problem-title">
            Eten plannen kost energie die je liever ergens anders aan geeft.
          </h2>
          <ul className="mt-6 grid gap-3 text-sm text-stone-700 sm:grid-cols-2 sm:text-base" data-testid="landing-problem-list">
            <li className="rounded-2xl bg-stone-50 p-4">Elke week opnieuw bedenken wat je gaat eten.</li>
            <li className="rounded-2xl bg-stone-50 p-4">In de winkel vergeet je net die ene zak rijst.</li>
            <li className="rounded-2xl bg-stone-50 p-4">Dubbele aankopen omdat je niet meer weet wat in huis is.</li>
            <li className="rounded-2xl bg-stone-50 p-4">Leftovers en pantry-items gaan langzaam over de datum.</li>
          </ul>
        </section>

        {/* Solution */}
        <section className="mt-14" data-testid="landing-solution">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700">Hoe Smartly werkt</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="landing-solution-title">
            Eén plek voor planner, pantry, boodschappenlijst en recepten.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-stone-600 sm:text-base" data-testid="landing-solution-body">
            Smartly verbindt wat je deze week wilt eten met wat je nog hebt staan en wat je moet halen. Resultaat: minder nadenken, minder verspilling en geen vergeten boodschappen.
          </p>
        </section>

        {/* Feature cards */}
        <section id="features" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-features">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <article key={title} className="rounded-[26px] border border-stone-200 bg-white/85 p-5" data-testid={`landing-feature-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
              <span className="grid h-10 w-10 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-stone-600">{body}</p>
            </article>
          ))}
        </section>

        {/* Free vs Premium comparison */}
        <section className="mt-16 rounded-[32px] border border-stone-200 bg-white/85 p-7 sm:p-10" data-testid="landing-comparison">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500">Free vs Premium</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="landing-comparison-title">
            Wat zit er in elk plan?
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-[24px] border border-stone-200 bg-stone-50 p-5" data-testid="landing-comparison-free">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-stone-600">Free</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700">
                {FREE_BULLETS.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                    <span>{bullet}</span>
                  </li>
                ))}
                {PREMIUM_BULLETS.filter((bullet) => !FREE_BULLETS.includes(bullet)).map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-stone-400">
                    <X className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[24px] border border-amber-200 bg-amber-50/70 p-5" data-testid="landing-comparison-premium">
              <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
                <Sparkles className="h-4 w-4" />
                Premium
              </p>
              <ul className="mt-4 space-y-2 text-sm text-stone-800">
                {PREMIUM_BULLETS.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="mt-16 scroll-mt-24" data-testid="landing-pricing">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700">Prijzen</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="landing-pricing-title">
            Simpel: één gratis plan, één Premium plan.
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <article className="rounded-[28px] border border-stone-200 bg-white p-6" data-testid="landing-pricing-free">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-stone-500">Free</p>
              <p className="mt-3 text-4xl font-semibold tracking-tight">€0</p>
              <p className="mt-1 text-sm text-stone-600">Basis maaltijdplanning</p>
              <Button
                onClick={() => navigate(startCtaTarget)}
                variant="outline"
                className="mt-5 h-12 w-full rounded-full border-stone-300 bg-white text-stone-800 hover:bg-stone-50"
                data-testid="landing-pricing-free-cta"
              >
                {user ? "Open app" : "Start gratis"}
              </Button>
            </article>

            <article className="rounded-[28px] border-2 border-emerald-700/30 bg-white p-6 ring-1 ring-emerald-700/10" data-testid="landing-pricing-premium">
              <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700">
                <Sparkles className="h-4 w-4" />
                Premium
              </p>
              <p className="mt-3 text-4xl font-semibold tracking-tight" data-testid="landing-pricing-premium-monthly">€7,99 <span className="text-base font-medium text-stone-500">/ maand</span></p>
              <p className="mt-1 text-sm text-stone-600" data-testid="landing-pricing-premium-annual">of €59 / jaar</p>
              <Button
                onClick={() => navigate(premiumCtaTarget)}
                className="mt-5 h-12 w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800"
                data-testid="landing-pricing-premium-cta"
              >
                {user ? "Bekijk Premium in profiel" : "Maak gratis account aan"}
              </Button>
              <p className="mt-3 text-xs text-stone-500">Maandelijks opzegbaar. Beheer je abonnement zelf via Stripe.</p>
            </article>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-16 scroll-mt-24" data-testid="landing-faq">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500">FAQ</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="landing-faq-title">
            Veelgestelde vragen
          </h2>
          <div className="mt-6 space-y-3">
            {FAQ_ITEMS.map((item, index) => (
              <details key={item.q} className="group rounded-[20px] border border-stone-200 bg-white/85 p-5" data-testid={`landing-faq-item-${index}`}>
                <summary className="cursor-pointer list-none text-base font-semibold text-stone-900 marker:hidden">
                  <span className="flex items-center justify-between gap-3">
                    <span>{item.q}</span>
                    <span className="text-stone-400 transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-stone-700">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200/80 bg-[#f4efe3]/60" data-testid="landing-footer">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-5 py-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-stone-600" data-testid="landing-footer-copy">
            © {new Date().getFullYear()} Smartly. Plan slimmer, verspil minder.
          </p>
          <nav className="flex flex-wrap items-center gap-4 text-xs text-stone-700">
            <Link to="/login" className="hover:text-stone-900" data-testid="landing-footer-login">Log in</Link>
            <Link to="/signup" className="hover:text-stone-900" data-testid="landing-footer-signup">Start gratis</Link>
            <Link to="/pricing" className="hover:text-stone-900" data-testid="landing-footer-pricing">Prijzen</Link>
            <Link to="/privacy" className="hover:text-stone-900" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-900" data-testid="landing-footer-terms">Voorwaarden</Link>
            <Link to="/refund-policy" className="hover:text-stone-900" data-testid="landing-footer-refund">Opzeggen &amp; restitutie</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
